import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles, Collapse, Box } from "@material-ui/core";
import { styled } from "@mui/material/styles";
import { IconButton } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import { TextField, Button } from "@material-ui/core";

import { KeyboardArrowDown, KeyboardArrowUp, Delete as DeleteIcon, Edit as EditIcon } from "@material-ui/icons";

import axios from "../../../config/axios";
import { returnOrderStatus } from "assets/constants/constants";
import { getOrder, removeItem } from "redux/action/order.action";
import { UploadImage } from "components/UploadImage";
import { isEmpty, round, uniqBy } from "lodash";
import { getImageUrl } from "utils";
import { buildImageSlides, getFirstSlideUrl } from "utils/imageSlides";
import ImageCarouselDialog from "components/ImageCarouselDialog";

const useStyles = makeStyles(theme => ({
  col1: {
    fontWeight: "500",
    minWidth: "110px",
    padding: "10PX"
  },
  row1: {
    display: "flex",
    margin: "11px 0px 11px 0px"
  },
  details: {
    display: "flex",
    flexDirection: "column",
    padding: "30px",
    width: "90%"
  },
  thumbnail: {
    width: "60px",
    height: "60px",
    objectFit: "cover",
    borderRadius: "4px",
    cursor: "pointer",
    border: "1px solid #ddd"
  },
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative"
  },
  imageModalClose: {
    position: "absolute",
    right: 8,
    top: 8,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    zIndex: 1
  },
  subItemImage: {
    width: "50px",
    height: "50px",
    objectFit: "cover",
    borderRadius: "4px",
    marginRight: "8px",
    border: "1px solid #ddd"
  },
  strikethrough: {
    textDecoration: "line-through",
    color: "#999"
  },
  sizeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  '&.MuiTableCell-body': {
    fontSize: 14
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover
  }
}));

export const ItemLists = props => {
  const { item } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [uploadBarcoeId, setUploadBarcodeId] = useState(null);
  const [inputImageUrl, setInputImageUrl] = useState(null);
  const [inputBarcodeId, setInputBarcodeId] = useState(null);
  const [loading, setLoading] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const ORDER_STATE = useSelector(state => state.order);

  const defectLabel = t("itemLists.defect", "Defect");

  const handleImageClick = targetItemOrUrl => {
    const slides = typeof targetItemOrUrl === "string"
      ? [{ url: targetItemOrUrl, label: null }]
      : buildImageSlides(targetItemOrUrl, defectLabel);
    if (slides.length) {
      setSelectedSlides(slides);
      setSlideIndex(0);
      setImageModalOpen(true);
    }
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedSlides([]);
    setSlideIndex(0);
  };

  const handleDeleteItem = useCallback(
    itemId => {
      const handleRemoveItemSuccess = () => {
        dispatch(getOrder({ id: ORDER_STATE.order._id, type: "return-order" }));
        enqueueSnackbar(t("itemLists.itemRemoved"), { variant: "success" });
      };
      const handleRemoveItemFailure = error => {
        enqueueSnackbar(error, { variant: "error" });
      };

      dispatch(removeItem({ orderType: "return-order", itemId, handleRemoveItemSuccess, handleRemoveItemFailure }));
    },
    [ORDER_STATE, dispatch, enqueueSnackbar, t]
  );

  const handleCloseItem = useCallback(() => {
    setInputImageUrl(null);
    setInputBarcodeId(null);
    setUploadBarcodeId(null);
  }, []);

  const handleUploadItem = async () => {
    const handleAddSubItemSuccess = () => {
      dispatch(getOrder({ id: ORDER_STATE.order._id, type: "return-order" }));
      enqueueSnackbar(t("itemLists.barcodeScanned"), { variant: "success" });
      handleCloseItem();
    };

    let errorMessage;
    if (isEmpty(inputImageUrl)) errorMessage = t("itemLists.invalidImageUrl");
    if (inputBarcodeId !== uploadBarcoeId) errorMessage = t("itemLists.barcodeNoMatch");

    if (!!errorMessage) {
      enqueueSnackbar(errorMessage, { variant: "error" });
      return;
    }

    setLoading(true);
    await axios
      .patch(`/admin/update-sub-items/return-order`, { item_id: item._id, barcode_id: `${inputBarcodeId}`, return_barcode_image_url: inputImageUrl })
      .then(res => {
        handleAddSubItemSuccess();
      })
      .catch(error => {
        enqueueSnackbar(error.response.data.message, { variant: "warning" });
        return error;
      });
    setLoading(false);
  };

  return (
    <React.Fragment>
      {uploadBarcoeId && (
        <Dialog fullWidth={true} maxWidth={"sm"} open={true} onClose={() => setUploadBarcodeId(null)} aria-labelledby="max-width-dialog-title">
          <DialogTitle id="max-width-dialog-title">{t("itemLists.scanBarcode")}</DialogTitle>
          <div className={classes.details}>
            <div className={classes.row1}>
              <div className={classes.col1}>{t("itemLists.barcodeId")}</div>
              <div style={{ width: "400px" }}>
                <TextField
                  id="outlined-size-small"
                  label={t("itemLists.barcodeId")}
                  rows={4}
                  onChange={e => setInputBarcodeId(e.target.value)}
                  value={inputBarcodeId}
                  variant="outlined"
                  fullWidth={true}
                />
              </div>
            </div>

            <div className={classes.row1}>
              <div className={classes.col1}>{t("itemLists.barcodeImage")}</div>
              <UploadImage setImageUrl={setInputImageUrl} setIsImageUploading={setLoading} setBarcodeID={setInputBarcodeId} />
            </div>
          </div>
          <DialogActions>
            <Button onClick={handleUploadItem} color="primary" disabled={loading}>
              {loading ? t("itemLists.loading") : t("itemLists.add")}
            </Button>

            <Button onClick={handleCloseItem} color="primary">
              {t("itemLists.close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <StyledTableRow key={item._id}>
        <StyledTableCell size="medium">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </StyledTableCell>

        <StyledTableCell>{item._id}</StyledTableCell>
        <StyledTableCell component="th" scope="row" align="center">
          {item.content_name}
        </StyledTableCell>
        <StyledTableCell align="center">{item.quantity}</StyledTableCell>
        <StyledTableCell align="center">
          <div className={classes.sizeContainer}>
            {item.size || item.original_size_m2 ? (
              <>
                {(item.size || item.original_size_m2) && (
                  <span className={item.measured_size_m2 && Math.abs((item.size || item.original_size_m2) - item.measured_size_m2) > 0.01 ? classes.strikethrough : ""}>
                    {round(item.size || item.original_size_m2, 1)} m²
                  </span>
                )}
                {item.measured_size_m2 && (
                  <span style={{ fontWeight: Math.abs((item.size || item.original_size_m2 || 0) - item.measured_size_m2) > 0.01 ? "600" : "normal" }}>
                    {round(item.measured_size_m2, 1)} m² {Math.abs((item.size || item.original_size_m2 || 0) - item.measured_size_m2) > 0.01 && "(measured)"}
                  </span>
                )}
              </>
            ) : (
              "-"
            )}
          </div>
        </StyledTableCell>
        <StyledTableCell align="center">
          {getFirstSlideUrl(item, defectLabel) ? (
            <img
              src={getFirstSlideUrl(item, defectLabel)}
              alt="Item"
              className={classes.thumbnail}
              onClick={() => handleImageClick(item)}
              onError={e => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            "-"
          )}
        </StyledTableCell>
        <StyledTableCell align="center">
          {item?.defects?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
              {item.defects.find(defect => defect.defect_image_url)?.defect_image_url && (
                <img
                  src={getImageUrl(item.defects.find(defect => defect.defect_image_url).defect_image_url)}
                  alt="Defect"
                  className={classes.thumbnail}
                  onClick={() => handleImageClick(item)}
                  onError={e => {
                    e.target.style.display = "none";
                  }}
                  style={{ border: "2px solid #ff9800" }}
                />
              )}
              <span style={{ fontSize: "0.85em", color: "#ff9800", fontWeight: "500" }}>
                {item.defects.length} {item.defects.length === 1 ? t("itemLists.defect", "defect") : t("itemLists.defects", "defects")}
              </span>
            </div>
          ) : (
            "-"
          )}
        </StyledTableCell>
        <StyledTableCell align="center">{item.product_id}</StyledTableCell>
        <StyledTableCell align="center">
          <Link to={`/admin/orders/${item.order_id}`}>
            <div style={{ fontWeight: "400" }}>{item.order_id}</div>
          </Link>
        </StyledTableCell>
        {ORDER_STATE.order.status === returnOrderStatus.processing && (
          <StyledTableCell align="center">
            <IconButton aria-label="expand row" size="small" onClick={() => handleDeleteItem(item._id)}>
              <DeleteIcon />
            </IconButton>
          </StyledTableCell>
        )}
      </StyledTableRow>

      {item.sub_items.length > 0 && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0, padding: "0px 74px" }} colSpan={9}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, padding: "50px" }}>
                {item.sub_items.filter(subItem => subItem.barcode_id).length > 0 && (
                  <Table size="small" aria-label="a dense table" style={{ marginBottom: "24px" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("itemLists.pickupBarcode")}</TableCell>
                        <TableCell>{t("itemLists.returnBarcode")}</TableCell>
                        <TableCell>{t("itemLists.imageUrl")}</TableCell>
                        <TableCell align="center">{t("itemLists.action")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uniqBy(item.sub_items.filter(subItem => subItem.barcode_id), '_id').map(subItem => (
                        <TableRow key={subItem._id}>
                          <TableCell>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {subItem.barcode_image_url && (
                                <img
                                  src={getImageUrl(subItem.barcode_image_url)}
                                  alt="Pickup barcode"
                                  className={classes.subItemImage}
                                  onClick={() => handleImageClick(getImageUrl(subItem.barcode_image_url))}
                                  onError={e => { e.target.style.display = "none"; }}
                                />
                              )}
                              <a href={getImageUrl(subItem.barcode_image_url)} target="_blank" rel="noopener noreferrer">
                                {subItem.barcode_id}
                              </a>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {subItem.return_barcode_image_url ? (
                                <>
                                  <img
                                    src={getImageUrl(subItem.return_barcode_image_url)}
                                    alt="Return barcode"
                                    className={classes.subItemImage}
                                    onClick={() => handleImageClick(getImageUrl(subItem.return_barcode_image_url))}
                                    onError={e => { e.target.style.display = "none"; }}
                                  />
                                  <a href={getImageUrl(subItem.return_barcode_image_url)} target="_blank" rel="noopener noreferrer">
                                    {subItem.barcode_id}
                                  </a>
                                </>
                              ) : (
                                "NA"
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {subItem.image_url ? (
                              <img
                                src={getImageUrl(subItem.image_url)}
                                alt="Item"
                                className={classes.subItemImage}
                                onClick={() => handleImageClick(item)}
                                onError={e => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell size="medium">
                            <IconButton aria-label="expand row" size="small" onClick={() => setUploadBarcodeId(subItem.barcode_id)}>
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
      <ImageCarouselDialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        slides={selectedSlides}
        initialIndex={slideIndex}
      />
    </React.Fragment>
  );
};
